import { approximateDistance, formatNumberWithSpaces, getLibMeasurementSystem, getMeasurementSystem, haversineDistance } from '../utils/Utils';
import { Connection, ConnectionTypes, createConnectionFromJson } from './Connection';
import { Coordinates } from './Coordinates';
import { OpeningTimeDay } from './OpeningTimeDay';
import { UserBadgeType } from './UserBadgeType';
import { UserType } from './UserType';

export interface PlebData {
  id: string;
  name: string;
  slug: string;
  merchant: boolean;
  bio?: string;
  profession?: string;
  website?: string;
  twitterSlug?: string;
  connection?: Connection;
  pictures: string[];
  image: string;
  thumbnail: string;
  coordinates?: Coordinates;
  city?: string;
  email?: string;
  subscription?: string;
  specialBadge?: string;
  nostr?: string;
  interests?: string;
  address?: string;
  phoneNumber?: string;
  zapAddress?: string;
  openingTimes: Record<string, any>;
  hideDistance?: boolean;
  wallet?: {
    lnurl: string;
  };
  yob?: number;
  memberNumber?: number;
}

export class Pleb {
  id: string;
  name: string;
  slug: string;
  merchant: boolean;
  bio?: string;
  profession?: string;
  website?: string;
  twitterSlug?: string;
  connection?: Connection;
  pictures: string[];
  image: string;
  thumbnail: string;
  coordinates?: Coordinates;
  city?: string;
  email?: string;
  subscription?: string;
  specialBadge?: string;
  nostr?: string;
  interests?: string;
  address?: string;
  phoneNumber?: string;
  zapAddress?: string;
  openingTimes: Record<string, OpeningTimeDay>;
  hideDistance?: boolean;
  wallet?: {
    lnurl: string;
  };
  yob?: number;
  memberNumber?: number;
  
  constructor(data: PlebData) {
    this.id = data.id;
    this.name = data.name;
    this.slug = data.slug;
    this.merchant = data.merchant;
    this.bio = data.bio;
    this.profession = data.profession;
    this.website = data.website;
    this.twitterSlug = data.twitterSlug;
    this.connection = data.connection;
    this.pictures = data.pictures;
    this.image = data.image;
    this.thumbnail = data.thumbnail;
    this.coordinates = data.coordinates;
    this.city = data.city;
    this.email = data.email;
    this.subscription = data.subscription;
    this.specialBadge = data.specialBadge;
    this.nostr = data.nostr;
    this.interests = data.interests;
    this.address = data.address;
    this.phoneNumber = data.phoneNumber;
    this.zapAddress = data.zapAddress;
    this.openingTimes = data.openingTimes;
    this.hideDistance = data.hideDistance;
    this.wallet = data.wallet;
    this.yob = data.yob;
    this.memberNumber = data.memberNumber;
  }

  get formattedSlug(): string {
    return `@${this.slug}`;
  }

  get badge(): UserBadgeType | null {
    switch (this.specialBadge ?? this.subscription) {
      case 'fiat-merchant-founder':
      case 'sats-merchant-founder':
        return UserBadgeType.FOUNDER;
      case 'sats-gigachad':
        return UserBadgeType.GIGACHAD;
      case 'sats-lifetime':
      case 'lifetime99.99':
        return UserBadgeType.LIFETIME;
      case 'Sponsor1':
      case 'Sponsor2':
      case 'sponsor1':
      case 'sponsor2':
        return UserBadgeType.SPONSOR;
      case 'veteran':
        return UserBadgeType.VETERAN;
      default:
        return null;
    }
  }

  get zapAddressPaymentUrl(): string | null {
    return this.zapAddress ? `lightning:${this.zapAddress}` : null;
  }

  get type(): UserType {
    return this.merchant ? UserType.merchant : UserType.pleb;
  }

  static createConnection(json: Record<string, any>, currentUserId: string): Connection {
    const userJson = json.user;
    const connectionJson = json.connection;

    let connection: Connection;
    if (connectionJson) {
      connection = createConnectionFromJson(connectionJson, {
        currentUserId,
        userId: userJson._id
      });
    } else if (currentUserId === userJson._id) {
      connection = new Connection({
        type: ConnectionTypes.MY_SELF
      });
    } else {
      connection = new Connection({
        type: ConnectionTypes.NOT_ESTABLISHED
      });
    }

    return connection
  }

  getLabelToShowSendToWallet(): string {
    /*if(this.zapAddress)
      return this.zapAddress;
    else*/
      return this.name;
  }

  getLnUrlToPayTo(): string | null {
    if(this.zapAddress)
      return this.zapAddress;
    if(this.wallet?.lnurl)
      return this.wallet.lnurl;
    return null;
  }

  shouldShowNameAndPicture(): boolean {
    /*if(this.zapAddress)
      return false;
    else*/
      return true;
  }

  static fromJson(json: Record<string, any>, currentUserId: string): Pleb {
    const userJson = json.user;
    const connection = Pleb.createConnection(json, currentUserId);

    const coordinatesList = userJson.location?.coordinates as number[] | undefined;
    let coordinates: Coordinates | undefined;
    if (coordinatesList?.length) {
      coordinates = {
        latitude: coordinatesList[1],
        longitude: coordinatesList[0]
      };
    }

    const openingTimesJson = userJson.openingTimes as Record<string, any> | undefined;
    const openingTimes = openingTimesJson
      ? Object.fromEntries(
          Object.entries(openingTimesJson).map(
            ([key, value]) => [key, OpeningTimeDay.fromJson(value)]
          )
        )
      : {};

    return new Pleb({
      id: userJson._id,
      name: userJson.name,
      slug: userJson.slug,
      merchant: userJson.merchant,
      bio: userJson.bio,
      profession: userJson.profession,
      website: userJson.website,
      twitterSlug: userJson.twitterSlug,
      connection,
      pictures: userJson.pictures.map((p: any) => p.url),
      image: userJson.publicPictureURL,
      thumbnail: userJson.publicThumbnailURL ?? userJson.publicPictureURL,
      coordinates,
      city: userJson.city,
      email: userJson.email,
      subscription: userJson.subscription?.id,
      specialBadge: userJson.specialBadge,
      nostr: userJson.nostrPublicKey,
      interests: userJson.interests,
      address: userJson.address,
      phoneNumber: userJson.phoneNumber,
      zapAddress: userJson.zapAddress,
      openingTimes,
      hideDistance: userJson.hideDistance,
      wallet: json.wallet ? {
        lnurl: json.wallet.lnurl
      } : undefined,
      yob: userJson.yob,
      memberNumber: userJson.memberNumber
    });
  }

  static currentUserFromJson(json: Record<string, any>): Pleb {
    const coordinatesList = json.location?.coordinates as number[] | undefined;
    let coordinates: Coordinates | undefined;
    if (coordinatesList?.length) {
      coordinates = {
        latitude: coordinatesList[1],
        longitude: coordinatesList[0]
      };
    }

    const openingTimesJson = json.openingTimes as Record<string, any> | undefined;
    const openingTimes = openingTimesJson
      ? Object.fromEntries(
          Object.entries(openingTimesJson).map(
            ([key, value]) => [key, OpeningTimeDay.fromJson(value)]
          )
        )
      : {};

    return new Pleb({
      id: json._id,
      name: json.name,
      slug: json.slug,
      merchant: json.merchant,
      bio: json.bio,
      profession: json.profession,
      website: json.website,
      twitterSlug: json.twitterSlug,
      connection: new Connection({
        type: ConnectionTypes.MY_SELF
      }),
      pictures: json.pictures.map((p: any) => p.url),
      image: json.publicPictureURL,
      thumbnail: json.publicThumbnailURL ?? json.publicPictureURL,
      coordinates,
      city: json.city,
      email: json.email,
      subscription: json.subscription?.id,
      specialBadge: json.specialBadge,
      nostr: json.nostrPublicKey,
      interests: json.interests,
      address: json.address,
      phoneNumber: json.phoneNumber,
      zapAddress: json.zapAddress,
      openingTimes,
      hideDistance: json.hideDistance,
      wallet: json.wallet,
      yob: json.yob,
      memberNumber: json.memberNumber
    });
  }

  toJson(): PlebData {
    return {
      id: this.id,
      name: this.name,
      slug: this.slug,
      merchant: this.merchant,
      bio: this.bio,
      profession: this.profession,
      website: this.website,
      twitterSlug: this.twitterSlug,
      pictures: this.pictures,
      image: this.image,
      thumbnail: this.thumbnail,
      coordinates: this.coordinates,
      city: this.city,
      email: this.email,
      subscription: this.subscription,
      specialBadge: this.specialBadge,
      nostr: this.nostr,
      interests: this.interests,
      address: this.address,
      phoneNumber: this.phoneNumber,
      zapAddress: this.zapAddress,
      openingTimes: {},
      hideDistance: this.hideDistance,
      wallet: this.wallet,
      yob: this.yob,
      memberNumber: this.memberNumber
    };
  }
}

export function getDistanceBetweenCoordinateAndPleb(coordinates: Coordinates, pleb: Pleb | PlebData) : string | null {
  if(!coordinates || !pleb.coordinates) {
    return null;
  }

  if(pleb.hideDistance) {
    return null;
  }

  const distanceInM = haversineDistance(coordinates.latitude, coordinates.longitude, pleb.coordinates.latitude, pleb.coordinates.longitude);

  return formatNumberWithSpaces(approximateDistance(distanceInM, getMeasurementSystem(), true)) + ' ' + getLibMeasurementSystem();
}

export function getDistanceBetweenPlebs(pleb1: Pleb | PlebData, pleb2: Pleb | PlebData) : string | null {
  if(pleb1.id === pleb2.id) {
    return null;
  }

  if(!pleb1.coordinates || !pleb2.coordinates) {
    return null;
  }

  if(pleb1.hideDistance || pleb2.hideDistance) {
    return null;
  }

  const distanceInM = haversineDistance(pleb1.coordinates.latitude, pleb1.coordinates.longitude, pleb2.coordinates.latitude, pleb2.coordinates.longitude);

  return formatNumberWithSpaces(approximateDistance(distanceInM, getMeasurementSystem(), true)) + ' ' + getLibMeasurementSystem();
}
